import { getVideoMediaStream, stopTracks } from './WebcamHelper'

// Shared owner of the warehouse operator's camera MediaStream.
//
// Both useCameraPreview and useWebcamRecorder subscribe here so that
// exactly one navigator.mediaDevices.getUserMedia call is in flight
// at a time. The previous design opened one stream per hook instance,
// which on devices that disallow concurrent capture caused the second
// getUserMedia to reject with NotReadableError and silently break the
// packing-proof recording.
//
// Pattern mirrors src/helpers/cameraEventQueue.js: module singleton
// with a __reset__ hook for tests.

const TEARDOWN_DEBOUNCE_MS = 50

class CameraStreamManager {
  constructor() {
    this.currentStream = null
    this.pendingAcquire = null
    this.subscribers = new Map()
    this.teardownTimer = null
    this.endedHandlers = new WeakMap()
  }

  __reset__() {
    this._cancelTeardown()
    if (this.currentStream) this._detachEndedHandlers(this.currentStream)
    this.currentStream = null
    this.pendingAcquire = null
    this.subscribers = new Map()
    this.endedHandlers = new WeakMap()
  }

  getCurrentStream() {
    return this.currentStream
  }

  subscribe(id, onStreamChange = null) {
    this._cancelTeardown()
    this.subscribers.set(id, { onStreamChange })

    if (this.currentStream && this._streamIsAlive(this.currentStream)) {
      return Promise.resolve(this.currentStream)
    }
    if (this.pendingAcquire) {
      return this.pendingAcquire
    }
    this.pendingAcquire = this._acquire()
    return this.pendingAcquire
  }

  release(id) {
    if (!this.subscribers.has(id)) return
    this.subscribers.delete(id)
    if (this.subscribers.size === 0) {
      this._scheduleTeardown()
    }
  }

  refreshIfDead() {
    if (this.currentStream && this._streamIsAlive(this.currentStream)) {
      return Promise.resolve(this.currentStream)
    }
    if (this.pendingAcquire) {
      return this.pendingAcquire
    }
    this._teardownStream()
    this.pendingAcquire = this._acquire({ notifyReason: 'acquired' })
    return this.pendingAcquire
  }

  async _acquire({ notifyReason = null } = {}) {
    try {
      const stream = await getVideoMediaStream()
      if (this.subscribers.size === 0) {
        stopTracks(stream)
        return null
      }
      this.currentStream = stream
      this._attachEndedHandlers(stream)
      if (notifyReason) this._notifyAll(stream, { reason: notifyReason })
      return stream
    } finally {
      this.pendingAcquire = null
    }
  }

  _attachEndedHandlers(stream) {
    const handler = () => this._handleTrackEnded(stream)
    const tracks = stream.getVideoTracks()
    tracks.forEach(track => track.addEventListener('ended', handler))
    this.endedHandlers.set(stream, { handler, tracks })
  }

  _detachEndedHandlers(stream) {
    const entry = this.endedHandlers.get(stream)
    if (!entry) return
    entry.tracks.forEach(track => track.removeEventListener('ended', entry.handler))
    this.endedHandlers.delete(stream)
  }

  _handleTrackEnded(stream) {
    if (this.currentStream !== stream) return
    this._detachEndedHandlers(stream)
    this.currentStream = null
    this._notifyAll(null, { reason: 'ended' })
  }

  _notifyAll(stream, info) {
    this.subscribers.forEach(({ onStreamChange }) => {
      if (!onStreamChange) return
      try {
        onStreamChange(stream, info)
      } catch (err) {
        console.error('[cameraStreamManager] subscriber callback threw', err)
      }
    })
  }

  _streamIsAlive(stream) {
    return stream.getVideoTracks().some(track => track.readyState === 'live')
  }

  _scheduleTeardown() {
    this._cancelTeardown()
    this.teardownTimer = setTimeout(() => {
      this.teardownTimer = null
      if (this.subscribers.size === 0) this._teardownStream()
    }, TEARDOWN_DEBOUNCE_MS)
  }

  _cancelTeardown() {
    if (this.teardownTimer != null) {
      clearTimeout(this.teardownTimer)
      this.teardownTimer = null
    }
  }

  _teardownStream() {
    if (!this.currentStream) return
    this._detachEndedHandlers(this.currentStream)
    stopTracks(this.currentStream)
    this.currentStream = null
  }
}

const cameraStreamManager = new CameraStreamManager()
export default cameraStreamManager
